import { IService, PriceTypeEnum } from '@torim-app/types';

export const resolvePriceType = (service: Pick<IService, 'priceType' | 'maxPrice' | 'price'>): PriceTypeEnum => {
  if (service.priceType) return service.priceType;
  return service.maxPrice && service.maxPrice !== service.price
    ? PriceTypeEnum.RANGE
    : PriceTypeEnum.FIXED;
};

export const isFromPrice = (service: Pick<IService, 'priceType' | 'maxPrice' | 'price'>): boolean =>
  resolvePriceType(service) === PriceTypeEnum.FROM;

// The price actually charged/shown for a service: its sale price when set, else the list price.
export const effectiveServicePrice = (service: Pick<IService, 'price' | 'discountedPrice'>): number =>
  typeof service.discountedPrice === 'number' ? service.discountedPrice : service.price;

// ── Per-employee (provider) price/duration overrides ────────────────────────
type WithOverrides = Pick<IService, 'price' | 'maxPrice' | 'discountedPrice' | 'providerOverrides'>;

export const providerOverrideFor = (service: { providerOverrides?: IService['providerOverrides'] }, providerId?: string) =>
  providerId ? service.providerOverrides?.find(o => o.providerId === providerId) : undefined;

// True when the service's length is described by steps rather than a flat duration.
export const isSteppedService = (service: { steps?: IService['steps'] }): boolean =>
  !!service.steps?.length;

// Wall-clock length (minutes) of a stepped service: every step counts, waiting steps
// included, since the visit still spans them.
export const serviceStepsDuration = (service: { steps?: IService['steps'] }): number =>
  (service.steps ?? []).reduce((sum, step) => sum + (step.duration || 0), 0);

/**
 * The shape needed to work out how long a service takes. Kept structural (and every field
 * optional) so the trimmed service snapshots stored on vouchers and bundles resolve through
 * the same helper as a full IService.
 */
type WithDuration = {
  duration?: number | null;
  providerOverrides?: IService['providerOverrides'];
  steps?: IService['steps'];
};

/**
 * Duration (minutes) for a service when performed by a given provider.
 * A stepped service stores `duration: null` and takes its length from the steps, which are
 * the same for every provider — so per-provider duration overrides apply to flat services
 * only. Mirrors the server's combinedStepsCalculation().
 */
export const resolveServiceDuration = (service: WithDuration, providerId?: string): number => {
  if (isSteppedService(service)) return serviceStepsDuration(service);
  return providerOverrideFor(service, providerId)?.duration ?? service.duration ?? 0;
};

// Effective price for a service when performed by a given provider (provider override wins,
// else the global sale/list price).
export const resolveServicePrice = (service: WithOverrides, providerId?: string): number =>
  providerOverrideFor(service, providerId)?.price ?? effectiveServicePrice(service);

export const resolveServiceMaxPrice = (service: WithOverrides, providerId?: string): number => {
  const ov = providerOverrideFor(service, providerId);
  return ov?.maxPrice ?? ov?.price ?? service.maxPrice ?? effectiveServicePrice(service);
};

// Min/max price a service can cost across ALL its providers — used to show a range when no
// specific provider has been chosen yet.
export const serviceProviderPriceRange = (service: WithOverrides): { min: number; max: number } => {
  const prices = [
    effectiveServicePrice(service),
    ...(service.providerOverrides ?? [])
      .map(o => o.price)
      .filter((p): p is number => typeof p === 'number')
  ];
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

// True when the service's price varies by provider (so callers should show a range).
export const serviceHasProviderPriceVariance = (service: Pick<IService, 'providerOverrides'>): boolean =>
  !!service.providerOverrides?.some(o => typeof o.price === 'number');

/**
 * Total length (minutes) of a set of services when performed by a given provider.
 * Mirrors the server's own combined-steps calculation.
 */
export const totalServicesDuration = (
  services: Array<WithDuration>,
  providerId?: string
): number =>
  services.reduce((acc, service) => acc + resolveServiceDuration(service, providerId), 0);

const formatAmount = (n: number) => `₪${n}`;

export const formatServicePrice = (
  service: Pick<IService, 'price' | 'maxPrice' | 'priceType' | 'discountedPrice' | 'providerOverrides'>,
  opts?: { freeLabel?: string; fromLabel?: string; providerId?: string }
): string => {
  const freeLabel = opts?.freeLabel ?? 'ללא עלות';
  const fromLabel = opts?.fromLabel ?? 'החל מ-';
  const providerId = opts?.providerId;
  const type = resolvePriceType(service);

  // A specific provider is chosen → show that provider's resolved price.
  if (providerId) {
    const price = resolveServicePrice(service, providerId);
    if (type === PriceTypeEnum.FROM) return `${fromLabel}${formatAmount(price)}`;
    if (price === 0) return freeLabel;
    const maxP = resolveServiceMaxPrice(service, providerId);
    if (type === PriceTypeEnum.RANGE && maxP !== price) return `${formatAmount(price)} - ${formatAmount(maxP)}`;
    return formatAmount(price);
  }

  // No provider chosen but the price varies by provider → show the cross-provider range.
  if (serviceHasProviderPriceVariance(service)) {
    const { min, max } = serviceProviderPriceRange(service);
    return min === max ? formatAmount(min) : `${formatAmount(min)} - ${formatAmount(max)}`;
  }

  const price = effectiveServicePrice(service);
  if (type === PriceTypeEnum.FROM)
    return `${fromLabel}${formatAmount(price)}`;
  if (price === 0)
    return freeLabel;
  if (type === PriceTypeEnum.RANGE && service.maxPrice && service.maxPrice !== price)
    return `${formatAmount(price)} - ${formatAmount(service.maxPrice)}`;
  return formatAmount(price);
};
